import { useEffect, useRef, useState, type ChangeEvent } from 'react';
import { useSearchParams } from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';
import { CameraIcon, SunIcon } from 'lucide-react';
import { toast } from 'sonner';
import { useSignedInAccount } from '../../contexts/AccountContext';
import { founder } from '../../data/content';
import { demoSamplePhoto } from '../../utils/accountApi';
import { photoConsent } from '../../data/policies';
import { useSite } from '../../contexts/SiteContext';
import { dayShort, diaryWeek, today } from '../../utils/account';
import { resizeImage } from '../../utils/image';
import { btnPrimary, btnQuiet } from '../../ui/styles';
import { PageTitle } from './PageTitle';
import { Switch } from './Switch';
import { DiaryPrompt } from './diary/DiaryPrompt';
import { DiaryDraft } from './diary/DiaryDraft';
import type { Draft } from './diary/DiaryDraft';
import { DiaryCompare } from './diary/DiaryCompare';

/** Fotodagboek: je begin vergeet je, je foto niet. */
export function PhotoDiary() {
  const {
    policy
  } = useSite();
  const {
    account,
    addDiaryEntry,
    deleteDiary,
    updateCustomer
  } = useSignedInAccount();
  const [saving, setSaving] = useState(false);
  const needsConsent = account.customer.photoStorageConsent !== photoConsent.version;
  const [consent, setConsent] = useState(false);
  const [confirmWithdraw, setConfirmWithdraw] = useState(false);
  const [params, setParams] = useSearchParams();
  const fileRef = useRef<HTMLInputElement>(null);
  const entries = [...account.diary].sort((a, b) => a.date.localeCompare(b.date));
  const start = entries[0]?.date;
  const [beforeId, setBeforeId] = useState<string | undefined>(entries[0]?.id);
  const [afterId, setAfterId] = useState<string | undefined>(entries[entries.length - 1]?.id);
  const [draft, setDraftState] = useState<Draft | null>(null);
  // De voorvertoning is een blob-URL: vrijgeven zodra hij vervangen wordt of het scherm sluit.
  const setDraft = (next: Draft | null) => setDraftState(prev => {
    if (prev && prev.preview !== next?.preview) URL.revokeObjectURL(prev.preview);
    return next;
  });
  const draftRef = useRef<Draft | null>(null);
  draftRef.current = draft;
  useEffect(() => () => {
    if (draftRef.current) URL.revokeObjectURL(draftRef.current.preview);
  }, []);
  const newDraft = (photo: Blob) => setDraft({
    photo,
    preview: URL.createObjectURL(photo),
    note: ''
  });
  const before = entries.find(e => e.id === beforeId) ?? entries[0];
  const after = entries.find(e => e.id === afterId) ?? entries[entries.length - 1];
  const week = diaryWeek(start, today());
  const hasEntries = entries.length > 0;
  const photoLabel = hasEntries ? `Foto van week ${week}` : 'Startfoto';

  // Vanaf het dashboard: meteen de foto-stap tonen. (Een bestandskiezer mag de browser niet vanzelf openen.)
  const [prompt, setPrompt] = useState(() => Boolean(params.get('nieuw')));
  useEffect(() => {
    if (params.get('nieuw')) {
      params.delete('nieuw');
      setParams(params, {
        replace: true
      });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- alleen bij binnenkomst de ?nieuw-vlag opruimen

  const openCamera = () => fileRef.current?.click();
  const onFile = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    // Geen bestand gekozen (kiezer weggeklikt): er gebeurt niets.
    if (!file) return;
    setPrompt(false);
    // Verkleind vóór het uploaden: sneller op mobiel en geen foto's van 8 MB op de server.
    try {
      newDraft(await resizeImage(file));
    } catch {
      toast.error('Deze foto kon niet worden geopend', {
        description: 'Probeer een andere, of maak er een met je camera.'
      });
    }
  };
  const save = async () => {
    if (!draft || saving) return;
    setSaving(true);
    const ok = await addDiaryEntry(draft.photo, draft.note.trim(), needsConsent && consent ? photoConsent.version : undefined);
    setSaving(false);
    if (!ok) return;
    setConsent(false);
    // Nieuwste foto rechts, start links.
    setAfterId(undefined);
    setBeforeId(entries[0]?.id);
    setDraft(null);
    toast.success(hasEntries ? `Foto van week ${week} bewaard` : 'Startfoto bewaard', {
      description: hasEntries ? 'Schuif hieronder om te vergelijken met je start.' : `Over ${policy.diaryEveryDays} dagen herinneren we je aan de volgende.`
    });
  };
  return <div data-id="element-6434" data-mp="3pwvz97i38u34">
      <PageTitle lead="Huid verandert langzaam, dus je ziet het zelf nauwelijks. Een foto om de paar weken laat zien wat er echt gebeurt. Alleen jij en je specialist zien ze. Ze worden verwijderd als je daarom vraagt, en anders twee jaar na je laatste contact." action={!draft && <button type="button" onClick={openCamera} className={`${btnPrimary} whitespace-nowrap`} data-id="element-6436" data-mp="3kyks54j6gxol">
              <CameraIcon className="h-4 w-4" strokeWidth={1.5} aria-hidden="true" data-id="element-6437" data-mp="1e84unuif2c1g" />
              {photoLabel}
            </button>} data-id="element-6435" data-mp="3pw32mzcufkyp" />
      <p className="-mt-5 mb-6 text-[13px]" data-id="element-6438" data-mp="33o74kdstli5r">
        <a href="#privacy" className="text-muted underline-offset-4 transition-colors duration-150 ease-soft hover:text-ink hover:underline" data-id="element-6439" data-mp="33oz05omf3fw1">
          Privacy en toestemming ›
        </a>
      </p>
      <input ref={fileRef} type="file" accept="image/*" capture="user" onChange={onFile} className="sr-only" tabIndex={-1} aria-hidden="true" data-id="element-6440" data-mp="3desayneqjqz8" />

      <AnimatePresence initial={false} data-id="element-6441" data-mp="38lh08b6syq21">
        {prompt && !draft && <DiaryPrompt week={week} hasEntries={hasEntries} onCamera={openCamera} onSample={demoSamplePhoto ? () => {
        setPrompt(false);
        void demoSamplePhoto?.().then(newDraft, () => toast.error('De voorbeeldfoto laadde niet'));
      } : undefined} data-id="element-6442" data-mp="3mcvksmgcyzb7" />}
      </AnimatePresence>

      <AnimatePresence initial={false} data-id="element-6443" data-mp="1x0a85wyq2ov3">
        {draft && <DiaryDraft draft={draft} title={hasEntries ? `Week ${week}` : 'Je startfoto'} saving={saving} needsConsent={needsConsent} consent={consent} onConsent={setConsent} onChange={setDraft} onSave={() => void save()} onRetake={openCamera} data-id="element-6444" data-mp="1x1v67wk8wpxp" />}
      </AnimatePresence>

      <div className="grid grid-cols-1 gap-8 xl:grid-cols-12" data-id="element-6445" data-mp="euam0f3i53f9">
        <section aria-labelledby="compare-title" className="xl:col-span-7" data-id="element-6446" data-mp="juf05ygxtm2b">
          <h2 id="compare-title" className="sr-only" data-id="element-6447" data-mp="ou1de7wrn2b4">
            Vergelijken
          </h2>
          <DiaryCompare entries={entries} before={before} after={after} onBefore={setBeforeId} onAfter={setAfterId} onNewPhoto={openCamera} data-id="element-6448" data-mp="tkrl3rsnzh8z" />
        </section>

        <aside className="space-y-6 xl:col-span-5" aria-label="Tips en tijdlijn" data-id="element-6449" data-mp="xozbdtert59b">
          <section className="bg-sand p-5 sm:p-6" aria-labelledby="diary-tips-title" data-id="element-6450" data-mp="12nj0g2onux5w">
            <h2 id="diary-tips-title" className="flex items-center gap-2 text-[15px] text-ink" data-id="element-6451" data-mp="17lub2fms5bp6">
              <SunIcon className="h-4 w-4 text-clay" strokeWidth={1.5} aria-hidden="true" data-id="element-6452" data-mp="1hpsjrhc26u6a" />
              Zo wordt het vergelijkbaar
            </h2>
            <ul className="mt-3 space-y-2 text-[14px] leading-relaxed text-muted" data-id="element-6453" data-mp="17qtkuqh3icwh">
              <li data-id="element-6454" data-mp="17sw9p76ihzzm">Steeds hetzelfde raam, overdag, zonder lamp aan.</li>
              <li data-id="element-6455" data-mp="13qjbr4y0b6de">Schone huid, geen make-up, haar uit je gezicht.</li>
              <li data-id="element-6456" data-mp="20fqpvrwaqd7a">Recht vooruit en één keer van de zijkant.</li>
            </ul>
          </section>

          {hasEntries && <section aria-labelledby="timeline-title" data-id="element-6457" data-mp="1vgxnauijs6lw">
              <h2 id="timeline-title" className="text-[15px] text-ink" data-id="element-6458" data-mp="1qpfn48ocma1z">
                Tijdlijn
              </h2>
              <ol className="mt-3 space-y-3" reversed data-id="element-6459" data-mp="1movo0zk7swd7">
                {[...entries].reverse().map(e => <li key={e.id} className="flex gap-3" data-id="element-6460" data-mp="2j70aaw7lumdn">
                    <img src={e.image} alt="" className="h-20 w-16 shrink-0 object-cover" data-id="element-6461" data-mp="2ef9zkp4r9wlp" />
                    <div className="min-w-0" data-id="element-6462" data-mp="2edfe99pko2h3">
                      <p className="text-[14px] text-ink" data-id="element-6463" data-mp="2y294d4yylb1k">
                        Week {diaryWeek(start, e.date)} <span className="text-muted" data-id="element-6464" data-mp="32u94axf143y5">· {dayShort(e.date)}</span>
                      </p>
                      {e.note && <p className="mt-0.5 text-[14px] leading-relaxed text-muted" data-id="element-6465" data-mp="2o6fiuw39i77n">{e.note}</p>}
                    </div>
                  </li>)}
              </ol>
            </section>}

          <section id="privacy" aria-labelledby="diary-privacy-title" className="scroll-mt-24 border-t border-line pt-5" data-id="element-6466" data-mp="3gubio9o4of8n">
            <h2 id="diary-privacy-title" className="mb-3 text-[15px] text-ink" data-id="element-6467" data-mp="3lqiom2gixqql">
              Privacy en toestemming
            </h2>
            <Switch checked={account.customer.diaryConsent} onChange={() => void updateCustomer({
            diaryConsent: !account.customer.diaryConsent
          })} label={`${founder.firstName} mag mijn foto’s anoniem laten zien`} hint={`Zonder je ogen of naam, als voorbeeld voor anderen. Standaard staat dit uit en je foto’s zijn alleen voor jou en ${founder.firstName}. Intrekken kan altijd.`} data-id="element-6468" data-mp="3bu5s6wtlgys5" />
          </section>

          {/* Toestemming om te bewaren intrekken = alles weg. Twee stappen, want dit is niet terug te draaien. */}
          {!needsConsent && <section className="border-t border-line pt-5" aria-labelledby="diary-withdraw-title" data-id="element-6469" data-mp="ni1kc0vvgczo">
              <h2 id="diary-withdraw-title" className="text-[15px] text-ink" data-id="element-6470" data-mp="3vxuzabak1gem">
                Toestemming om foto’s te bewaren
              </h2>
              {confirmWithdraw ? <div className="mt-2" role="alert" data-id="element-6471" data-mp="8jzefk1dbff9">
                  <p className="text-[14px] leading-relaxed text-ink" data-id="element-6472" data-mp="8lu1p5043jmi">
                    {entries.length === 1 ? 'Je foto wordt' : `Al je ${entries.length} foto’s worden`} definitief verwijderd, ook bij {founder.firstName}. Dit kun je niet terugdraaien.
                  </p>
                  <div className="mt-3 flex flex-wrap gap-2" data-id="element-6473" data-mp="386pjtx8j0q4u">
                    <button type="button" className={btnPrimary} onClick={() => void deleteDiary().then(ok => {
                setConfirmWithdraw(false);
                if (ok) toast.success('Toestemming ingetrokken', {
                  description: 'Je foto’s zijn verwijderd.'
                });
              })} data-id="element-6474" data-mp="jxwtbte37sf">
                      Ja, verwijder alles
                    </button>
                    <button type="button" className={btnQuiet} onClick={() => setConfirmWithdraw(false)} data-id="element-6475" data-mp="38yg0l1cas168">
                      Terug
                    </button>
                  </div>
                </div> : <>
                  <p className="mt-1 text-[14px] leading-relaxed text-muted" data-id="element-6476" data-mp="2uzgosu9vvgrh">Je gaf toestemming om je foto’s te bewaren. Intrekken verwijdert ze allemaal.</p>
                  <button type="button" onClick={() => setConfirmWithdraw(true)} className={`${btnQuiet} -ml-3 mt-2 text-muted`} data-id="element-6477" data-mp="2q1oobbv6ambo">
                    Toestemming intrekken
                  </button>
                </>}
            </section>}
        </aside>
      </div>
    </div>;
}